import { ActionIcon, Group, Paper, Stack, Text } from '@mantine/core'
import { IconChevronLeft, IconChevronRight, IconClock, IconTrendingUp, IconUser, IconUsers } from '@tabler/icons-react'
import { useMemo } from 'react'

import { StatInfoGraphic } from '@/components/StatInfoGraphic'
import type * as T from '@/services/api/api.types'
import { formatBillingDate } from '@/utils/analytics'

// Constants
const PERIOD_STATUS = {
  CURRENT: 'Last',
  PAST: 'Past',
} as const

const TOOLTIPS = {
  CREDITS_USED: 'Total credits used during this 30-day period',
  DAILY_AVERAGE: 'Average credits used per day',
  UNIQUE_USERS: 'Number of unique users during this period',
  AVERAGE_PER_USER: 'Average credits per user during this period',
} as const

interface BillingStatsPanelProps {
  data: T.GetBillingCreditsDataResponse
  onNavigateToPrevious: () => void
  onNavigateToNext: () => void
  canGoBack: boolean
  canGoForward: boolean
  startDate: string
  endDate: string
}

export function BillingStatsPanel({
  data,
  onNavigateToPrevious,
  onNavigateToNext,
  canGoBack,
  canGoForward,
  startDate,
  endDate,
}: BillingStatsPanelProps) {
  // Calculate metrics from the provided data
  const metrics = useMemo(
    () => ({
      totalCredits: data.totalActiveCredits || 0,
      averageCredits: data.averageCredits || 0,
      uniqueUserCount: data.uniqueUserCount || 0,
      averagePerUserCredits: data.averagePerUserCredits || 0,
    }),
    [data],
  )

  const periodStatus = useMemo(() => {
    // If we can't go forward, we're at the current period (last 30 days including today)
    return canGoForward ? PERIOD_STATUS.PAST : PERIOD_STATUS.CURRENT
  }, [canGoForward])

  return (
    <Paper radius="md" p="lg" shadow="sm" px="xl">
      <Group gap="md" justify="space-between">
        {/* Period Navigation Header */}
        <Stack justify="flex-end">
          <Group justify="flex-end" align="center" gap="md">
            <ActionIcon variant="light" size="lg" onClick={onNavigateToPrevious} disabled={!canGoBack}>
              <IconChevronLeft size={18} />
            </ActionIcon>

            <Group gap="xs" align="center">
              <Text size="lg" fw={600}>
                {formatBillingDate(startDate, true)}
              </Text>
              <Text size="lg" c="dimmed">
                —
              </Text>
              <Text size="lg" fw={600}>
                {formatBillingDate(endDate, true)}
              </Text>
            </Group>

            <ActionIcon variant="light" size="lg" onClick={onNavigateToNext} disabled={!canGoForward}>
              <IconChevronRight size={18} />
            </ActionIcon>
          </Group>

          <Group justify="center">
            <Text size="xs" c="dimmed" fw={500}>
              {periodStatus} 30-Day Period
            </Text>
          </Group>
        </Stack>
        {/* Statistics */}
        <Group justify="space-between" align="top">
          <Group gap="xl">
            <StatInfoGraphic
              icon={IconClock}
              label="Credits Used"
              value={metrics.totalCredits.toLocaleString()}
              tooltipText={TOOLTIPS.CREDITS_USED}
            />
            <StatInfoGraphic
              icon={IconTrendingUp}
              label="Daily Average"
              value={metrics.averageCredits}
              tooltipText={TOOLTIPS.DAILY_AVERAGE}
            />
            <StatInfoGraphic
              icon={IconUsers}
              label="Unique Users"
              value={metrics.uniqueUserCount.toLocaleString()}
              tooltipText={TOOLTIPS.UNIQUE_USERS}
            />
            <StatInfoGraphic
              icon={IconUser}
              label="Avg Per User"
              value={metrics.averagePerUserCredits.toFixed(1)}
              tooltipText={TOOLTIPS.AVERAGE_PER_USER}
            />
          </Group>
        </Group>
      </Group>
    </Paper>
  )
}
